import type { InteracoesPorMes, OverviewGeral, OverviewGraficos, TipoInteracaoCs } from "./api";
import type { Serie } from "./GraficoLinhas";

// Formatação, séries dos gráficos e linhas de apoio dos KPIs, compartilhadas
// entre a Home e o modo TV (as duas renderizam as mesmas telas).

const numero = new Intl.NumberFormat("pt-BR");
export const formatInteiro = (v: number) => numero.format(v);
export const formatNumero = (v?: number | null) => (v == null ? "—" : numero.format(v));
/** Para a TV: a partir de 1 milhão abrevia ("7,92 mi", "1,23 bi") para caber no quadro do KPI. */
const compacto = new Intl.NumberFormat("pt-BR", { notation: "compact", maximumSignificantDigits: 3 });
export const formatCompacto = (v?: number | null) =>
  v == null ? "—" : Math.abs(v) >= 1_000_000 ? compacto.format(v) : numero.format(v);
export const formatPercentual =(v: number) => `${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

/** Cores semânticas: verde = bom (ativos), amarelo = atenção (trials), vermelho = perda
 * (cancelados). Mesmas nos gráficos e nos números. */
export const CORES = {
  verde: "#1baf7a",
  amarelo: "#eab308",
  vermelho: "#e5484d",
  azul: "#2a78d6",
  roxo: "#8b5cf6",
} as const;

// Linhas de apoio dos KPIs. undefined = sem dado, a linha não aparece.

/** "+3 no mês", "−2 no mês", "+0 no mês". */
export function detalheVariacao(
  variacao: number | null | undefined,
  sufixo: string,
  formatar: (v: number) => string = formatInteiro,
): string | undefined {
  if (variacao == null) return undefined;
  const sinal = variacao < 0 ? "−" : "+";
  return `${sinal}${formatar(Math.abs(variacao))} ${sufixo}`;
}

/** Variação líquida: ativos de hoje menos os da última coleta do mês anterior (pode ser negativa). */
export function detalheAtivos(geral?: OverviewGeral, graficos?: OverviewGraficos): string | undefined {
  const ativos = geral?.ativos;
  const base = graficos?.ativosFimMesAnterior;
  if (ativos == null || base == null) return undefined;
  return detalheVariacao(ativos - base, "no mês");
}

export function detalheCancelamentos(geral?: OverviewGeral): string | undefined {
  const { ativos, cancelamentosAnoAtual, cancelamentosMesAtual } = geral ?? {};
  const partes = [
    detalheVariacao(cancelamentosMesAtual, "no mês"),
    ativos && cancelamentosAnoAtual != null
      ? `${formatPercentual((cancelamentosAnoAtual / ativos) * 100)} dos ativos`
      : undefined,
  ].filter(Boolean);
  return partes.length ? partes.join(" · ") : undefined;
}

export interface DadosGrafico {
  meses: string[];
  series: Serie[];
}

export function seriesAbsoluto(graficos?: OverviewGraficos): DadosGrafico | undefined {
  const absoluto = graficos?.graficoAbsoluto;
  if (!absoluto) return undefined;
  return {
    meses: absoluto.label,
    series: [
      { key: "ativos", label: "Ativos totais", valores: absoluto.serieTotalAtivos, cor: CORES.verde },
      { key: "trials", label: "Trials no mês", valores: absoluto.serieTrialsMes, cor: CORES.amarelo },
      { key: "cancelados", label: "Cancelados no mês", valores: absoluto.serieCanceladosMes, cor: CORES.vermelho },
    ],
  };
}

/** Tipos da tela CS; as automáticas (CA, SA) ficam de fora. */
export const TIPOS_CS: { tipo: TipoInteracaoCs; label: string; cor: string }[] = [
  { tipo: "SU", label: "Suporte", cor: CORES.azul },
  { tipo: "CB", label: "Cobrança", cor: CORES.amarelo },
  { tipo: "CN", label: "Contato", cor: CORES.verde },
  { tipo: "TR", label: "Treinamento", cor: CORES.roxo },
];

export function seriesInteracoes(dados?: InteracoesPorMes): DadosGrafico | undefined {
  if (!dados) return undefined;
  return {
    meses: dados.meses,
    series: TIPOS_CS.map(({ tipo, label, cor }) => ({ key: tipo, label, valores: dados.series[tipo] ?? [], cor })),
  };
}

/** Mês atual (último ponto) e a diferença para o mês passado (penúltimo). */
export function mesAtualEVariacao(valores?: number[]): { atual?: number; variacao?: number } {
  const atual = valores?.at(-1);
  const anterior = valores?.at(-2);
  return { atual, variacao: atual != null && anterior != null ? atual - anterior : undefined };
}
